import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FiPlus, FiCpu, FiGitBranch, FiZap, FiAward, FiPackage, FiMessageSquare, FiList, FiBarChart2, FiTarget } from 'react-icons/fi';

// Multi-tool probe prompts — exercise the full query→draft→dispatch→
// synthesize loop. Distinct from "Quick Prompts" which are single-
// purpose templates; probes are workflow validations. Adding a new
// probe is a one-line push into this array.
interface ProbePrompt {
  label: string;
  title: string;
  prompt: string;
}
const PROBE_PROMPTS: ProbePrompt[] = [
  {
    label: 'Blocker report + peer review',
    title:
      'Find attention items, draft a report, dispatch to 3 peers, synthesize. '
      + 'Exercises attention_items + consult_peers + synthesis — the full '
      + 'query→draft→dispatch→synthesize loop. ~100-120s, 5 rounds.',
    prompt:
      'Find the current blockers in the task system, draft a brief written report, '
      + 'ask 3 colleagues to review it, then give me a synthesized recommendation.',
  },
];

// 🚧 Lab templates — abstract, context-aware versions of the
// exploratory probes. Each one is intentionally generic: scope drawn
// from "our current thread" / "what we have" so the persona can run
// the template as-is using ambient chat context, OR the user can drop
// it into the compose box and tweak the specifics before sending.
// Tucked behind a sub-flyout (clicked from the main menu) so the
// operator's daily compose chrome stays lean.
const LAB_TEMPLATES: ProbePrompt[] = [
  {
    label: '🧭 Multi-peer deep dive',
    title:
      'Picks 3 items from current chat context, surfaces gaps, consults 2 specialist '
      + 'peers, synthesizes one concrete move. Runs as-is or paste-and-tweak.',
    prompt:
      'Pick the 3 most-recently-touched items relevant to our current thread '
      + '(or ask me to pick if it\'s ambiguous). For each, surface its current '
      + 'state, recent activity, and any visible gaps. Then consult 2 specialist '
      + 'peers about which gap is the most urgent to close, and synthesize their '
      + 'takes into one concrete next move I can take today.',
  },
  {
    label: '🌗 Contrarian retrospective',
    title:
      'Summarizes recent activity in current focus area, then has 2 peers with '
      + 'opposing takes argue about which anomaly matters most. Synthesizes the winner.',
    prompt:
      'Summarize the last 7 days of activity in our current focus area '
      + '(or ask me to scope it if unclear). Flag anomalies — what shifted, '
      + 'what stalled, what surprised you. Then consult 2 peers with deliberately '
      + 'contrasting takes and have them disagree about which anomaly matters '
      + 'most. Tell me whose argument was stronger and why, with one concrete '
      + 'follow-up.',
  },
  {
    label: '🛠 Capability gap audit',
    title:
      'Inventories available skills/tools + pending suggestions, identifies top '
      + '3 gaps, drafts proposals, peer-reality-checks, returns ranked list.',
    prompt:
      'Inventory the skills/tools currently available to us, then pull any pending '
      + 'suggestions or backlog signals you can find. Identify the top 3 capability '
      + 'gaps. Draft a one-paragraph proposal for closing each. Ask 1 peer to '
      + 'reality-check the list, then return a ranked top-3 with the reasoning '
      + 'for each rank visible.',
  },
  {
    label: '👻 Ghost rescue',
    title:
      'Scans for incomplete projects/items, picks the most urgent, generates a '
      + 'handoff note, consults 2 peers, synthesizes one unblocking action.',
    prompt:
      'Scan for projects/items with high incompleteness — ghost nodes, stale '
      + 'stubs, abandoned threads (you pick the right query for what we have). '
      + 'Pick the most urgent one. Read its current state, generate a handoff '
      + 'note, ask 2 peers what single change would unblock it, and synthesize '
      + 'one concrete next action I can take today.',
  },
  {
    label: '🗺 Mindmap of unknowns',
    title:
      'Asks 3 peers same question (or one derived from thread), generates a '
      + 'Mermaid mindmap of their answers, ends with one concrete next step.',
    prompt:
      'Ask 3 peers the same question: "what\'s the single biggest unknown in our '
      + 'current approach?" — or derive a more pointed version of that question '
      + 'from this thread if more relevant. Generate a Mermaid mindmap of the '
      + 'union of their answers. End with the one concrete next step you\'d take '
      + 'to close the most central unknown.',
  },
  {
    label: '⚖ ADR from debate',
    title:
      'Proposes 3 alternatives for current decision, has 2 opposing peers debate, '
      + 'writes it up as an ADR artifact with tradeoffs + recommendation.',
    prompt:
      'Propose 3 alternative approaches for the decision we\'ve been circling '
      + '(or ask me what decision you should anchor on if unclear). Consult 2 '
      + 'peers with opposing viewpoints on which to pick. Based on their '
      + 'arguments, write the result up as an ADR artifact (use create_artifacts '
      + 'with type="adr") with tradeoffs captured. End with your recommendation '
      + 'and the reasoning for picking it.',
  },
];

// Construction-zone barber-pole stripes. Inline style because Tailwind
// can't express a `repeating-linear-gradient` with arbitrary stops. Used
// as the top + bottom band of the 🚧 Lab sub-flyout — the visual signal
// that the contents are experimental templates, not regular menu items.
const CONSTRUCTION_STRIPES = {
  backgroundImage:
    'repeating-linear-gradient(45deg, #fbbf24 0 8px, #1f2937 8px 16px)',
};

interface ComposeMenuProps {
  personaId?: string;
  personaName?: string;
  personaTags?: string[];
  sessionId?: string;
  onInsert: (text: string) => void;
}

const DIAGRAM_TEMPLATES: Record<string, string> = {
  flow: '```mermaid\nflowchart TD\n    A[Start] --> B{Decision}\n    B -->|Yes| C[Process]\n    B -->|No| D[Alternative]\n    C --> E[End]\n    D --> E\n```\n',
  seq: '```mermaid\nsequenceDiagram\n    actor User\n    participant System\n    User->>System: Request\n    System-->>User: Response\n```\n',
  class: '```mermaid\nclassDiagram\n    class Entity {\n        +String name\n        +method()\n    }\n```\n',
  er: '```mermaid\nerDiagram\n    ENTITY_A ||--o{ ENTITY_B : "relates"\n    ENTITY_B ||--|{ ENTITY_C : "contains"\n```\n',
  state: '```mermaid\nstateDiagram-v2\n    [*] --> Idle\n    Idle --> Active: trigger\n    Active --> Done: complete\n    Done --> [*]\n```\n',
};

const ComposeMenu: React.FC<ComposeMenuProps> = ({
  personaId,
  personaName = 'AI',
  personaTags = [],
  sessionId,
  onInsert,
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // 🚧 Lab sub-flyout open state. Lives alongside `open` so the Lab can
  // remain expanded while the user reads the templates — closing the
  // main menu also closes the Lab (single click-outside handler covers
  // both because the Lab JSX is nested inside menuRef).
  const [showLab, setShowLab] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowLab(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Closing the main menu also collapses the Lab so the next open
  // starts from a clean state. Resetting on every close avoids the
  // surprise of "Lab silently re-opens because it was open last time."
  useEffect(() => {
    if (!open) setShowLab(false);
  }, [open]);

  const insert = (text: string) => {
    onInsert(text);
    setOpen(false);
    setShowLab(false);
  };

  const sectionHeader = `px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
    darkMode ? 'text-gray-500' : 'text-gray-400'
  }`;
  const itemClass = `block w-full text-left px-3 py-1.5 text-xs transition-colors ${
    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
  }`;
  const displayName = personaName.replace(/_/g, ' ');

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
          open
            ? darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
            : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        <FiPlus className="w-3 h-3" /> Compose
      </button>

      {open && (
        <div className={`absolute left-0 bottom-full mb-1 w-72 rounded-lg shadow-xl z-20 border overflow-hidden ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Quick Prompts */}
          <div className={sectionHeader}>Quick Prompts</div>
          <button className={itemClass} onClick={() => insert(`As ${displayName}, give me your deepest analysis of `)}>
            <FiCpu className="inline w-3 h-3 mr-1.5 text-emerald-400" /> Deep dive analysis...
          </button>
          <button className={itemClass} onClick={() => insert('Please analyze the following:\n\n```\n[paste here]\n```\n\nFocus on key insights, issues, and recommendations.')}>
            <FiBarChart2 className="inline w-3 h-3 mr-1.5 text-blue-400" /> Analyze pasted content
          </button>
          <button className={itemClass} onClick={() => insert('Compare and contrast these approaches:\n\n**Option A:** \n\n**Option B:** \n\nEvaluate on: feasibility, cost, timeline, and risk.')}>
            <FiGitBranch className="inline w-3 h-3 mr-1.5 text-purple-400" /> Compare approaches
          </button>
          <button className={itemClass} onClick={() => insert(`What are your top 3 recommendations for improving `)}>
            <FiList className="inline w-3 h-3 mr-1.5 text-amber-400" /> Top recommendations for...
          </button>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Multi-tool Probes — canned prompts that validate the full
              workflow: query → draft → dispatch → synthesize. Drop into
              compose for review/edit before sending. */}
          <div className={sectionHeader}>Multi-tool Probes</div>
          {PROBE_PROMPTS.map((p, i) => (
            <button
              key={i}
              className={itemClass}
              title={p.title}
              onClick={() => insert(p.prompt)}
            >
              <FiTarget className="inline w-3 h-3 mr-1.5 text-rose-400" />
              {p.label}
            </button>
          ))}

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* 🚧 Lab — single entry that opens a sub-flyout to the right
              with the 6 abstract / context-aware multi-tool templates.
              Tucked away here so the main menu stays lean for daily
              compose, but the templates live one click deep with
              construction-zone visuals to telegraph "experimental." */}
          <button
            className={`block w-full text-left px-3 py-1.5 text-xs transition-colors ${
              showLab
                ? darkMode
                  ? 'bg-amber-900/30 text-amber-300'
                  : 'bg-amber-50 text-amber-700'
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setShowLab((v) => !v)}
            aria-expanded={showLab}
            aria-controls="compose-lab-flyout"
            title="Multi-tool templates — abstract / context-aware. Run as-is or tweak."
          >
            <span className="inline-flex items-center w-full">
              <span className="mr-1.5">🚧</span>
              <span className="font-semibold tracking-wide">Lab</span>
              <span className={`ml-1.5 text-[10px] uppercase tracking-widest ${
                darkMode ? 'text-amber-500/70' : 'text-amber-600/80'
              }`}>
                · {LAB_TEMPLATES.length} templates
              </span>
              <span className="ml-auto opacity-60">{showLab ? '◂' : '▸'}</span>
            </span>
          </button>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Diagrams */}
          <div className={sectionHeader}>Diagrams</div>
          <div className="flex gap-1 px-3 py-1.5">
            {Object.entries(DIAGRAM_TEMPLATES).map(([key, tmpl]) => (
              <button
                key={key}
                onClick={() => insert(tmpl)}
                className={`px-2 py-1 rounded text-[10px] font-medium ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={`Insert ${key} diagram`}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Power Actions */}
          <div className={sectionHeader}>Conversation Actions</div>
          <button className={itemClass} onClick={() => insert('Please summarize our entire conversation so far. Include: key topics discussed, decisions made, and any open questions.')}>
            <FiMessageSquare className="inline w-3 h-3 mr-1.5 text-emerald-400" /> Summarize conversation
          </button>
          <button className={itemClass} onClick={() => insert('Extract all action items, decisions, and concrete next steps from our conversation. Format as a checklist.')}>
            <FiList className="inline w-3 h-3 mr-1.5 text-cyan-400" /> Extract action items
          </button>
          <button className={itemClass} onClick={() => insert('Based on our conversation, generate 3-5 probe questions that would test whether a persona truly understands the concepts we discussed.')}>
            <FiZap className="inline w-3 h-3 mr-1.5 text-amber-400" /> Generate probe questions
          </button>

          {/* Demoted from chip bar 2026-04-24 — situational patterns
              that earn a click here but don't deserve always-on chip
              real estate. Emoji prefix preserves their chip-bar
              identity for users who recognize them. */}
          <button
            className={itemClass}
            title="Protocol-driven session: persona asks one clarifying question at a time until it has enough to produce a deliverable."
            onClick={() =>
              insert(`Let's run a focused working session.

Protocol:
1. Ask me **one** clarifying question at a time — never a wall of questions
2. Wait for my answer before the next question
3. Continue until you have enough to produce a concrete deliverable (plan, draft, decision doc, code, whatever fits)
4. When you have enough, say "I have what I need" and produce it
5. If I go off-topic, gently pull us back

Start by asking me: **what's the deliverable, and when do I need it by?**`)
            }
          >
            <FiTarget className="inline w-3 h-3 mr-1.5 text-emerald-400" /> 🎯 Focus Session
          </button>
          <button
            className={itemClass}
            title="Generates a copy-pasteable handoff note for another persona picking up this thread cold."
            onClick={() =>
              insert(`Write a handoff note for another persona picking up this thread cold. Format:

**Handoff: <one-line topic>**

1. **Where we are** — the state of the work, in 2–3 sentences
2. **What's decided** — bullet list of decisions already made (with my reasoning for each)
3. **What's open** — bullet list of unresolved questions or choices
4. **What they need to know about me** — preferences, constraints, context that will make them effective from minute one
5. **Recommended next move** — what you'd do first if you were continuing

Keep it copy-pasteable. Minute-one effectiveness is the goal.`)
            }
          >
            <FiList className="inline w-3 h-3 mr-1.5 text-purple-400" /> 📝 Brief Next Person
          </button>
          <button
            className={itemClass}
            title="Surfaces the top 3 ways the current approach goes wrong, with severity + what would prevent each."
            onClick={() =>
              insert(`What's the most likely way our current approach goes wrong?

List the top 3 failure modes:
1. **Mode** + a one-line description of what triggers it
2. **Severity** (critical / major / minor) + what we lose if it happens
3. **What would prevent it** — concrete, actionable

End with the single failure mode you'd most want to defend against first, and why.`)
            }
          >
            <FiBarChart2 className="inline w-3 h-3 mr-1.5 text-rose-400" /> 🪤 Failure Modes
          </button>

          {/* Platform Links */}
          {personaId && (
            <>
              <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={sectionHeader}>Platform</div>
              <button className={itemClass} onClick={() => { setOpen(false); navigate(`/personas/forge?personaId=${personaId}`); }}>
                <FiPackage className="inline w-3 h-3 mr-1.5 text-purple-400" /> Open in Forge
              </button>
              <button className={itemClass} onClick={() => { setOpen(false); navigate('/gymnasium/arena'); }}>
                <FiAward className="inline w-3 h-3 mr-1.5 text-amber-400" /> Test in Gymnasium
              </button>
            </>
          )}
        </div>
      )}

      {/* 🚧 Lab sub-flyout — opens to the right of the main menu when
          "Lab" is clicked from inside it. Construction-zone visuals
          (amber + barber-pole stripe bands) telegraph "experimental
          templates." Same click-outside handler dismisses both menus
          because this lives inside menuRef. */}
      {open && showLab && (
        <div
          id="compose-lab-flyout"
          role="region"
          aria-label="Lab — multi-tool templates"
          className={`absolute left-72 bottom-full mb-1 ml-2 w-80 rounded-lg shadow-2xl z-30 border overflow-hidden ${
            darkMode
              ? 'bg-gray-900 border-amber-500/40'
              : 'bg-amber-50 border-amber-300'
          }`}
        >
          {/* Top construction band */}
          <div className="h-2 w-full" style={CONSTRUCTION_STRIPES} aria-hidden />

          {/* Header */}
          <div className={`px-3 py-2 ${darkMode ? 'bg-gray-900' : 'bg-amber-50'}`}>
            <div
              className={`flex items-center gap-1.5 text-sm font-bold ${
                darkMode ? 'text-amber-300' : 'text-amber-800'
              }`}
            >
              <span aria-hidden>🚧</span>
              <span className="tracking-wider">LAB</span>
              <span
                className={`ml-auto text-[9px] uppercase tracking-widest font-mono ${
                  darkMode ? 'text-amber-500/60' : 'text-amber-700/70'
                }`}
              >
                experimental
              </span>
            </div>
            <div
              className={`text-[10px] mt-0.5 ${
                darkMode ? 'text-amber-200/60' : 'text-amber-700/80'
              }`}
            >
              Multi-tool templates · paste &amp; tweak or run as-is
            </div>
          </div>

          {/* Templates list */}
          <div className={`px-1 py-1 ${darkMode ? 'bg-gray-900/95' : 'bg-white/70'}`}>
            {LAB_TEMPLATES.map((t, i) => (
              <button
                key={`lab-${i}`}
                onClick={() => insert(t.prompt)}
                title={t.title}
                className={`block w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                  darkMode
                    ? 'text-gray-200 hover:bg-amber-900/30 hover:text-amber-200'
                    : 'text-gray-800 hover:bg-amber-100 hover:text-amber-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Bottom construction band */}
          <div className="h-2 w-full" style={CONSTRUCTION_STRIPES} aria-hidden />
        </div>
      )}
    </div>
  );
};

export default ComposeMenu;
